// PrescriptionUpload.tsx
import React, { ChangeEvent, useRef, useState } from "react";
import PageWrapper from "../components/layout/PageWrapper";
import HttpClient, { HttpMutationOutput } from "../lib/HttpClient";
import { useSnackbarContext } from "../contexts/Snackbar";
import {
  Button,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextareaAutosize,
  CircularProgress,
} from "@material-ui/core";
import { GrUpload } from "react-icons/gr";

// new imports — adjust path if necessary
import MedicineTableRow, {
  Medicine,
} from "../components/prescriptionUpload/MedicineTableRow";
import { fileToBase64 } from "../Utils";
import {
  parseMedicineInstructionField,
  processMedicineInstructionField,
} from "../components/prescriptionUpload/stateUtils";
import utils from "../Utils";
import HealthCareProviderProfile from "../models/domain/HealthCareProviderProfile";
import PatientSearchAutocomplete from "../components/prescriptionUpload/PatientSearchAutocomplete";
import { InfoOutlined } from "@material-ui/icons";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, parseISO } from "date-fns";

const allowedFileTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];

const invalid_report_err_msg =
  "You can only upload photos (PNG, JPG, JPEG) or PDF files. Audio and Video files are not supported. Please try again.";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 12,
    height: "100%",
    boxSizing: "border-box" as const,
    marginTop: 80,
  },
  leftPanel: {
    height: "calc(100vh - 80px)",
    border: "1px solid var(--border-color)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  },
  uploadBox: {
    width: 520,
    height: "100%",
    border: "1px dashed rgba(255,255,255,0.12)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column" as const,
    textAlign: "center" as const,
    padding: 20,
    background: "transparent",
  },
  selectImageBtn: {
    marginTop: 12,
  },
  rightPanel: {
    height: "calc(100vh - 80px)",
    overflowY: "auto" as const,
    padding: 16,
  },
  section: {
    marginBottom: 18,

    paddingBottom: 6,
    borderBottom: "1px solid #eee",
  },
  medsRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 8,
  },
  medInput: {
    flex: 1,
  },
  smallInput: {
    width: 120,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 12,
    marginBottom: 12,
  },
  tinyLabel: {
    color: "#6b7280",
    marginBottom: 4,
    fontWeight: 500,
    fontSize: 20,
  },
  container: {
    background: "#f8f9fb",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.5,

    marginBottom: theme.spacing(1.5),
  },
  segmentedWrapper: {
    background: "#eef2f7",
    borderRadius: 10,
    padding: 4,
  },
  button: {
    flex: 1,
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 500,
    boxShadow: "none",
    border: "2px solid transparent", // ← Always reserve space
  },

  activeButton: {
    background: "#ffffff",
    borderColor: "#1976d2",
    color: "#1976d2",
    fontWeight: 600,
    "&:hover": {
      background: "#ffffff",
      boxShadow: "none",
    },
  },

  inactiveButton: {
    background: "transparent",
    borderColor: "transparent",
    color: "#4b5563",
    "&:hover": {
      background: "transparent",
      boxShadow: "none",
    },
  },
  disabledSection: {
    opacity: 0.6,
    pointerEvents: "none",
    filter: "grayscale(40%)",
  },
  disabledNotice: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff8e1", // soft yellow
    border: "1px solid #ffe082",
    color: "#8d6e63",
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    fontSize: 14,
    marginTop: theme.spacing(1.5),
  },
  noticeIcon: {
    marginRight: theme.spacing(1),
    fontSize: 18,
    opacity: 0.8,
  },
}));

// If the imported component exports a type, this will match it.
// If not, this local interface is a compatible fallback.
type LocalMedicineTemplate = Medicine & {
  drugName?: string;
  drugType?: string;
  strength?: string | number;
  strengthUnit?: string;
  instruction?: string;
  frequencyPerWeek?: string;
  freqMorn?: number | string;
  freqAfter?: number | string;
  freqEvening?: number | string;
  freqNight?: number | string;
  duration?: string | number;
  durationUnit?: string;
  beforeFood?: any;
};

const createEmptyTemplate = (id?: string | number): LocalMedicineTemplate => ({
  drugName: "",
  drugType: "",
  strength: "",
  strengthUnit: "NA",
  instruction: "",
  frequencyPerWeek: "Everyday",
  freqMorn: 0,
  freqAfter: 0,
  freqEvening: 0,
  freqNight: 0,
  duration: "",
  durationUnit: "null",
  beforeFood: "",
});

const mapApiMedicineToLocal = (apiMed: any): LocalMedicineTemplate => {
  const parsedInstruction = parseMedicineInstructionField(apiMed?.instruction);

  return {
    drugName: apiMed?.drugName ?? "",
    drugType: apiMed?.drugType ?? "Tablet",

    strength: apiMed?.strength || "",

    strengthUnit: apiMed?.unit || "",

    freqMorn: apiMed?.frequency?.[0] ?? 0,
    freqAfter: apiMed?.frequency?.[1] ?? 0,
    freqNight: apiMed?.frequency?.[2] ?? 0,
    freqEvening: apiMed?.frequency?.[3] ?? 0,

    frequencyPerWeek: apiMed?.frequencyPerWeek ?? "Everyday",

    duration:
      apiMed?.duration && apiMed.duration !== "NA"
        ? Number(apiMed.duration.replace(/[^0-9]/g, ""))
        : 0,

    durationUnit:
      apiMed?.duration && apiMed.duration !== "NA"
        ? apiMed.duration.replace(/[0-9]/g, "").trim()
        : "null",

    instruction: parsedInstruction.instructionText,
    beforeFood: parsedInstruction.instructionDropdownOption,

    category: apiMed?.category ?? "OTHER",
    composition: apiMed?.composition ?? "",
  };
};

const PrescriptionUpload: React.FC = () => {
  const { setSnackbar } = useSnackbarContext();

  const classes = useStyles();

  const [doctorProfile, setDoctorProfile] = useState<HealthCareProviderProfile>(
    utils.retrieveSelectDoctorProfile(),
  );

  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [prescriptionType, setPrescriptionType] = useState<
    "PRINTED" | "HANDWRITTEN"
  >("PRINTED");
  // left upload state
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // right form simple state

  const [patientSearch, setPatientSearch] = useState("");

  const [patientName, setPatientName] = useState("");
  const [patientAge, setPatientAge] = useState("");
  const [patientGender, setPatientGender] = useState("");
  const [patientId, setPatientId] = useState("");

  const [prescriptionDate, setPrescriptionDate] = useState<Date | null>(null);

  const [symptomNotes, setSymptomNotes] = useState("");

  const isUploadDisabled = isExtracting || !patientId || !patientName;

  // medicine templates state — use advanced template rows
  const [medicines, setMedicines] = useState<LocalMedicineTemplate[]>([]);

  // handlers for upload UI
  const onSelectImageClick = () => {
    if (isUploadDisabled) return;
    fileInputRef.current?.click();
  };
  const handleClearAll = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    setUploadedFile(null);
    setMedicines([]);
    setSymptomNotes("");
    setPatientName("");
    setPatientAge("");
    setPatientGender("");
    setPatientId("");
    setPrescriptionDate(null);
    setPatientSearch("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelection = async (file: File) => {
    if (!allowedFileTypes.includes(file.type)) {
      setSnackbar?.({
        message: invalid_report_err_msg,
        severity: "error",
      });
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setIsExtracting(true); // 👈 start loading

    setUploadedFile(file);
    setPreviewUrl(URL.createObjectURL(file));

    setSnackbar?.({
      message: "Image selected. Extracting prescription…",
      severity: "info",
    });

    try {
      const result = await extractPrescriptionFromImage(
        file,
        doctorProfile.id,
        patientId,
        prescriptionType,
      );
      if (!result.success) throw new Error(result.error);

      const apiMedicines = result?.data?.body?.medicines ?? [];
      setMedicines(apiMedicines.map(mapApiMedicineToLocal));

      result?.data?.body?.note && setSymptomNotes(result.data.body.note);
      result?.data?.body?.prescriptionDate &&
        setPrescriptionDate(parseISO(result.data.body.prescriptionDate));

      setSnackbar?.({
        message: "Prescription extracted successfully",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar?.({
        message: err.message || "Extraction failed",
        severity: "error",
      });
    } finally {
      setIsExtracting(false); // 👈 stop loading
    }
  };
  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    handleFileSelection(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    handleFileSelection(f);
  };

  const onDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  const handleTemplateChange = (
    index: number,
    templateOrKey?: LocalMedicineTemplate | string,
    value?: any,
  ) => {
    setMedicines((prev) => {
      if (index < 0 || index >= prev.length) return prev;

      const copy = [...prev];

      // If key is a string → update a single property
      if (typeof templateOrKey === "string") {
        copy[index] = {
          ...copy[index],
          [templateOrKey]: value,
        };
      } else if (templateOrKey) {
        // Replace entire object
        copy[index] = templateOrKey;
      }

      return copy;
    });
  };

  const handleDeleteTemplate = (index: number) => {
    setMedicines((prev) => prev.filter((_, i) => i !== index));
  };

  const addTemplateRow = () =>
    setMedicines((prev) => [...prev, createEmptyTemplate()]);

  const onConfirmUpload = async () => {
    if (!uploadedFile) {
      setSnackbar?.({
        message: "Please upload an image before confirming",
        severity: "error",
      });
      return;
    }
    console.log("Saving prescription for patientId:", patientId);

    if (!patientId) {
      setSnackbar?.({
        message: "Invalid Patient ID",
        severity: "error",
      });
      return;
    }

    setIsSaving(true); // 👈 start saving

    setSnackbar?.({
      message: "Saving prescription...",
      severity: "info",
    });

    const payload = {
      complaintChips: { customNote: symptomNotes },
      date: prescriptionDate
        ? format(prescriptionDate, "yyyy-MM-dd")
        : undefined,
      medicines: medicines?.map((item) => ({
        drugName: item.drugName?.trim(),
        strength: (item.strength + " " + item.strengthUnit)?.trim(),
        frequency: [
          item.freqMorn || 0,
          item.freqAfter || 0,
          item.freqNight || 0,
          item.freqEvening || 0,
        ],
        frequencyPerWeek: item.frequencyPerWeek,
        duration:
          item?.duration != null && item?.duration !== ""
            ? String(item.duration) + (item?.durationUnit ?? "")
            : undefined,
        instruction:
          processMedicineInstructionField(
            item.beforeFood,
            item.instruction,
          )?.trim() || "",
        drugType: item.drugType,
      })),
    };

    try {
      const saveResponse: any = await HttpClient.performPost(
        "/api/v1/users/prescriptions/handwritten/" + patientId,
        payload,
      );

      if ((saveResponse?.body as any)?.status?.success) {
        setSnackbar?.({
          message: "Prescription saved successfully",
          severity: "success",
        });
      } else {
        setSnackbar?.({
          message: saveResponse?.body?.status?.message || "Save failed",
          severity: "error",
        });
      }
    } catch (e) {
      setSnackbar?.({
        message: "Save failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false); // 👈 stop saving
    }
  };

  interface ExtractPrescriptionPayload {
    base64Image: string;
    doctorId: string;
    patientId: string;
    prescriptionType: string;
  }

  interface ExtractPrescriptionResponse {
    success: boolean;
    data?: any;
    error?: string;
  }

  const extractPrescriptionFromImage = async (
    file: File,
    doctorId: string,
    patientId: string,
    prescriptionType: string,
  ): Promise<ExtractPrescriptionResponse> => {
    try {
      const imageBase64 = await fileToBase64(file);

      const payload: ExtractPrescriptionPayload = {
        base64Image: imageBase64,
        doctorId: doctorId,
        patientId: patientId,
        prescriptionType: prescriptionType,
      };

      const url = `/api/v1/utility/prescription/extract?doctorId=${doctorId}`;

      const resp = await HttpClient.performPost(url, payload);
      if ((resp?.body as any)?.status?.success) {
        return {
          success: true,
          data: resp,
        };
      }

      return {
        success: false,
        error:
          (resp?.body as any)?.status?.message ||
          "Prescription extraction failed",
      };
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || "Prescription extraction failed",
      };
    }
  };

  const handleClearImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setUploadedFile(null);

    // reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <PageWrapper>
      <div className={classes.root}>
        <Grid container spacing={2}>
          {/* LEFT: big upload area */}
          <Grid item xs={12} md={3}>
            <Paper className={classes.leftPanel} elevation={0}>
              <div
                className={`${classes.uploadBox} ${
                  isUploadDisabled ? classes.disabledSection : ""
                }`}
                onDrop={!isUploadDisabled ? onDrop : undefined}
                onDragOver={!isUploadDisabled ? onDragOver : undefined}
                onDragLeave={!isUploadDisabled ? onDragLeave : undefined}
                style={{
                  borderColor: dragOver ? "rgba(255,165,0,0.9)" : undefined,
                  backgroundColor: dragOver
                    ? "rgba(255,165,0,0.03)"
                    : undefined,
                }}
              >
                {previewUrl ? (
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={previewUrl}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: 6,
                      }}
                    />

                    {isExtracting && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(0,0,0,0.35)",
                          borderRadius: 6,
                        }}
                      >
                        <CircularProgress
                          size={42}
                          thickness={4}
                          style={{ color: "#fff", marginBottom: 12 }}
                        />

                        <Typography
                          style={{
                            color: "#fff",
                            fontSize: 18,
                            fontWeight: 600,
                            padding: "6px 14px",
                            background: "rgba(0,0,0,0.6)",
                            borderRadius: 6,
                            letterSpacing: 0.3,
                          }}
                        >
                          Extracting prescription…
                        </Typography>
                      </div>
                    )}

                    {!isExtracting && (
                      <button
                        onClick={handleClearImage}
                        className="mt-4 btn btn-secondary"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <GrUpload size={46} style={{ opacity: 0.9 }} />
                    <Typography variant="body1" style={{ marginTop: 12 }}>
                      Drag & drop prescription image here
                    </Typography>
                    <Typography variant="caption" style={{ opacity: 0.7 }}>
                      or click to browse files
                    </Typography>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={onFileChange}
                      disabled={isUploadDisabled}
                      style={{ display: "none" }}
                    />

                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isUploadDisabled}
                      className={classes.selectImageBtn}
                      onClick={onSelectImageClick}
                    >
                      {isExtracting
                        ? "Extracting..."
                        : "Select Prescription Image"}
                    </Button>
                    {!patientId && (
                      <div className={classes.disabledNotice}>
                        <InfoOutlined className={classes.noticeIcon} />
                        Select a patient first to enable upload
                      </div>
                    )}
                  </>
                )}
              </div>
            </Paper>
          </Grid>

          {/* RIGHT: simple extracted form */}
          <Grid item xs={12} md={9}>
            <Paper className={classes.rightPanel} elevation={0}>
              <div className={classes.section}>
                <Typography className={classes.label}>
                  Prescription Type
                </Typography>

                <div className={classes.segmentedWrapper}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        className={`${classes.button} ${
                          prescriptionType === "PRINTED"
                            ? classes.activeButton
                            : classes.inactiveButton
                        }`}
                        onClick={() => setPrescriptionType("PRINTED")}
                      >
                        Printed Prescription
                      </Button>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        className={`${classes.button} ${
                          prescriptionType === "HANDWRITTEN"
                            ? classes.activeButton
                            : classes.inactiveButton
                        }`}
                        onClick={() => setPrescriptionType("HANDWRITTEN")}
                      >
                        Handwritten Prescription
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </div>
              <div className={classes.section}>
                <Typography variant="h6">Patient Profile</Typography>
              </div>
              <Grid container>
                <Grid container spacing={2} style={{ marginBottom: 12 }}>
                  <Grid item sm={12}>
                    <PatientSearchAutocomplete
                      patientName={patientSearch}
                      onNameChangeHandler={setPatientSearch}
                      onSelectHandler={(patient) => {
                        if (!patient) return;
                        setPatientSearch("");
                        setPatientName(patient.name);
                        setPatientId(patient.id ?? "");
                        setPatientAge(patient.age ?? "");
                        setPatientGender(patient.gender ?? "");
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InputLabel shrink className={classes.tinyLabel}>
                      Patient Name
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Patient Name"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputLabel shrink className={classes.tinyLabel}>
                      Patient ID
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Patient ID"
                      value={patientId}
                      onChange={(e) => setPatientId(e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputLabel shrink className={classes.tinyLabel}>
                      Patient Age
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Patient Age"
                      value={patientAge}
                      onChange={(e) => setPatientAge(e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputLabel shrink className={classes.tinyLabel}>
                      Patient Gender
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Patient Gender"
                      value={patientGender}
                      onChange={(e) => setPatientGender(e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>

              <div className={classes.section} style={{ marginTop: 16 }}>
                <Typography variant="h6">Extracted Data</Typography>
              </div>

              <div className={classes.section}>
                <InputLabel shrink className={classes.tinyLabel}>
                  Prescription Date
                </InputLabel>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={prescriptionDate}
                    onChange={(date) => setPrescriptionDate(date)}
                    variant="inline"
                    margin="none"
                  />
                </MuiPickersUtilsProvider>
              </div>

              <div className={classes.section}>
                <InputLabel shrink className={classes.tinyLabel}>
                  Diagnosis / Complaints
                </InputLabel>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Extracted diagnosis and complaints will appear here"
                  value={symptomNotes}
                  onChange={(e) => setSymptomNotes(e.target.value)}
                  style={{ width: "100%", padding: 8, fontSize: 14 }}
                  className="form-control"
                />
              </div>

              {/* Medicines section: advanced rows (table) */}
              <div className={classes.section}>
                <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                  Medicines
                </Typography>

                <Table size="small" aria-label="medicines-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Strength</TableCell>
                      <TableCell>Dosage</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Instruction</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {medicines?.map((tmpl, index) => (
                      <MedicineTableRow
                        key={index} // OK if list order is stable
                        medicine={tmpl}
                        onChangeHandler={(
                          template: any,
                          key?: any,
                          value?: any,
                        ) => {
                          // child may send (template, key, value)
                          // or (updatedTemplate)
                          if (typeof key === "string") {
                            handleTemplateChange(index, key, value);
                          } else {
                            handleTemplateChange(
                              index,
                              template as LocalMedicineTemplate,
                            );
                          }
                        }}
                        onDeleteHandler={() => handleDeleteTemplate(index)}
                      />
                    ))}
                  </TableBody>
                </Table>

                <Button
                  size="small"
                  style={{ marginTop: 6, marginLeft: "auto" }}
                  onClick={addTemplateRow}
                >
                  + Add Medicine
                </Button>
              </div>

              <div className={classes.actions}>
                <button
                  className="btn btn_outline"
                  disabled={isSaving || isExtracting}
                  onClick={handleClearAll}
                >
                  Clear
                </button>

                <button
                  className="btn btn_primary"
                  disabled={isSaving || isExtracting}
                  onClick={onConfirmUpload}
                >
                  {isSaving ? "Saving..." : "Save Prescription"}
                </button>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </PageWrapper>
  );
};

export default PrescriptionUpload;
